import React, { useEffect, useMemo, useRef } from 'react';
import '../../styles/intro.css';

const NBSP = String.fromCharCode(160);

/**
 * Scroll-driven letter explosion landing.
 * A fixed overlay shows the name + catchphrase; an in-flow spacer provides the
 * scroll distance. Scroll progress through the spacer drives --p (0 → 1), each
 * letter scatters by its own random vector * --p, and the overlay fades out
 * exactly as the page content reaches the top — so there's no empty gap.
 */
const ScrollIntro = ({ name, phrase }) => {
  const overlayRef = useRef(null);
  const spacerRef = useRef(null);

  const makeLetters = (text) => {
    const out = [];
    for (let i = 0; i < text.length; i++) {
      const raw = text.charAt(i);
      const angle = Math.random() * Math.PI * 2;
      const dist = 180 + Math.random() * 360;
      out.push({
        ch: raw === ' ' ? NBSP : raw,
        enter: (i * 0.022).toFixed(3),
        tx: Math.round(Math.cos(angle) * dist),
        ty: Math.round(Math.sin(angle) * dist - 40),
        rot: Math.round(Math.random() * 260 - 130)
      });
    }
    return out;
  };

  const nameLetters = useMemo(() => makeLetters(name), [name]);
  const phraseLetters = useMemo(() => makeLetters(phrase), [phrase]);

  useEffect(() => {
    const overlay = overlayRef.current;
    const spacer = spacerRef.current;
    if (!overlay || !spacer) return;
    const scroller = document.getElementById('root') || window;
    let raf = 0;

    const update = () => {
      raf = 0;
      const denom = Math.max(spacer.offsetHeight, 1);
      // spacer rect top goes from 0 to -denom as it scrolls past the top.
      const p = Math.min(Math.max(-spacer.getBoundingClientRect().top / denom, 0), 1);
      overlay.style.setProperty('--p', p.toFixed(4));
      overlay.classList.toggle('intro-gone', p >= 0.995);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const t = setTimeout(() => overlay.classList.add('intro-show'), 60);

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, []);

  const renderLetters = (arr) =>
    arr.map((l, i) => (
      <span key={i} className="intro-letter" aria-hidden="true" style={{ '--enter': l.enter + 's' }}>
        <span
          className="intro-letter-i"
          style={{ '--tx': l.tx + 'px', '--ty': l.ty + 'px', '--rot': l.rot + 'deg' }}
        >
          {l.ch}
        </span>
      </span>
    ));

  return (
    <>
      <div className="intro-overlay" ref={overlayRef} aria-hidden="true">
        <div className="intro-bg" />
        <div className="intro-inner">
          <h1 className="intro-name">{renderLetters(nameLetters)}</h1>
          <p className="intro-phrase">{renderLetters(phraseLetters)}</p>
        </div>
        <span className="intro-scroll">
          scroll to explode<span className="arrow">↓</span>
        </span>
      </div>
      <div className="intro-spacer" ref={spacerRef} aria-hidden="true" />
    </>
  );
};

export default ScrollIntro;
